import { useEffect, useRef, useState } from "react";
import { useEventsStore } from "@/stores/events.store";
import { eventsService } from "@/services";
import type { StompEventResponse, EventSeverity } from "@/services/types";

const ALARM_SEVERITIES: ReadonlySet<EventSeverity> = new Set<EventSeverity>([
  "WARNING",
  "CRITICAL",
]);
const AUTO_DISMISS_MS = 8000;
const EVAL_DEBOUNCE_MS = 120;

interface AlarmSite {
  id: number;
  name: string;
}

interface StompEventWithSite extends StompEventResponse {
  site?: AlarmSite;
}

function alarmKey(e: StompEventResponse): string {
  return `${e.eventId}|${e.severity ?? ""}`;
}

export function useEventAlarm() {
  const [alarm, setAlarm] = useState<StompEventResponse | null>(null);
  const [site, setSite] = useState<AlarmSite | null>(null);
  const seenRef = useRef<string | null>(null);

  useEffect(() => {
    const initial = useEventsStore.getState().stompEvents[0];
    seenRef.current = initial ? alarmKey(initial) : null;

    let pending: StompEventWithSite | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let seq = 0;

    const evaluate = async () => {
      const ev = pending;
      pending = null;
      if (!ev) return;
      const mySeq = ++seq;

      let severity = ev.severity;
      let resolvedSite: AlarmSite | null = ev.site ?? null;

      if ((!severity || !resolvedSite) && Number.isFinite(ev.id)) {
        try {
          const detail = (await eventsService.getEvent(ev.id)).data;
          severity = severity ?? detail?.severity;
          if (!resolvedSite && detail?.site) {
            resolvedSite = { id: detail.site.id, name: detail.site.name };
          }
        } catch {
          return;
        }
      }

      if (mySeq !== seq) return;
      if (!severity || !ALARM_SEVERITIES.has(severity)) return;
      setAlarm({ ...ev, severity });
      setSite(resolvedSite);
    };

    const unsub = useEventsStore.subscribe((state) => {
      const latest = state.stompEvents[0] as StompEventWithSite | undefined;
      if (!latest || alarmKey(latest) === seenRef.current) return;
      seenRef.current = alarmKey(latest);
      pending = latest;
      clearTimeout(timer);
      timer = setTimeout(evaluate, EVAL_DEBOUNCE_MS);
    });

    return () => {
      clearTimeout(timer);
      unsub();
    };
  }, []);

  useEffect(() => {
    if (!alarm) return;
    const t = setTimeout(() => setAlarm(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [alarm]);

  return { alarm, site, dismiss: () => setAlarm(null) };
}
